import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/presentation/components/common/Icon';
import type { ReactNode } from 'react';

interface ReportDetailHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Destino del link de volver (por defecto dashboard). Ignorado si hay onBack. */
  backTo?: string;
  /** Texto del botón/link de volver. */
  backLabel?: string;
  /** Si se pasa, el “volver” es un botón (p. ej. regresar a la tabla del mismo reporte). */
  onBack?: () => void;
}

const backClassName =
  'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700 transition hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300';

/** Cabecera de reporte alineada a DomainPageHeader (pregunta/eyebrow + título + acciones). */
export function ReportDetailHeader({
  title,
  description,
  actions,
  backTo = '/',
  backLabel,
  onBack,
}: ReportDetailHeaderProps) {
  const { t } = useTranslation();
  const label = backLabel ?? t('reports.actions.backToHub', { defaultValue: 'Volver al inicio' });

  return (
    <header className="bi-domain-header flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-1">
        {onBack ? (
          <button type="button" onClick={onBack} className={backClassName}>
            <Icon path="M15 18l-6-6 6-6" className="h-4 w-4" />
            {label}
          </button>
        ) : (
          <Link to={backTo} className={backClassName}>
            <Icon path="M15 18l-6-6 6-6" className="h-4 w-4" />
            {label}
          </Link>
        )}
        <h1 className="text-xl font-semibold tracking-tight text-gray-950 dark:text-white sm:text-2xl">{title}</h1>
        {description && <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-400">{description}</p>}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </header>
  );
}
